import stringifierTypes from "@/stringifierTypes";
import DataFieldCache from "@/models/data.field.cache";

import {translate} from "@/auxillary/translation";
import {findEnumValue} from "@/auxillary/enum.lookup";
import ReadonlyDataField from "@/models/ReadonlyDataField";

export interface IStringifier {
   (field: DataFieldCache | ReadonlyDataField): string;
}

export const stringify = function(field: DataFieldCache | ReadonlyDataField | undefined): string {
    if (field === undefined || !field.currentValues.value)
        return "";
    if (field.currentValues.unknown)
        return "unbekannt";
    if (field.currentValues.ongoing)
        return "andauernd";

    const stringifier = resolveStringifier(field.definitionType?.toString());
    return stringifier(field);
}

const resolveStringifier = function (fieldType: string): IStringifier {
    // type by dictionary else console.warn
    // @ts-ignore
    if (fieldType in stringifierTypes) return stringifierTypes[fieldType];
    else {
        console.warn("No stringifier with type: " + fieldType);
        return stringifierTypes.Default;
    }
}

export const DefaultStringifier :IStringifier = function (field: DataFieldCache | ReadonlyDataField): string {
    return field.currentValues.value?.toString();
}

export const DateStringifier: IStringifier = function (field: DataFieldCache | ReadonlyDataField): string {
    if (field.currentValues.year)
        return (field.currentValues.day ? field.currentValues.day : "xx")
            + "." + (field.currentValues.month ? field.currentValues.month : "xx")
            + "." + (field.currentValues.year)
    else
        return "";
}

export const EnumerationStringifier: IStringifier  = function (field: DataFieldCache | ReadonlyDataField): string {
    // Stored values are looked up by code without filtering isObsolete: a value recorded before the
    // option was retired must still render. Only the enum set itself is de-duplicated.
    const enumValue = findEnumValue(field.definition?.enum || field.definition.enum, field.currentValues.value);
    if (!enumValue?.displayKey && field.currentValues.value == "unknown")
        return "unbekannt";
    return enumValue?.displayKey ? translate(enumValue?.displayKey) : field.currentValues.value;
}

export const LabValueStringifier: IStringifier  = function (field: DataFieldCache | ReadonlyDataField): string {
    const unitEnumValue = findEnumValue(field.definition?.unitEnum || field.definition.unitEnum, field.currentValues.unit);
    const unitValue = translate(unitEnumValue?.displayKey);
    return (field.currentValues.value ?? "") + " " + (unitValue ?? "");
}

export const ValueWithUnitStringifier: IStringifier  = function (field: DataFieldCache | ReadonlyDataField): string {
    return (field.currentValues.value ?? "") + " " + (translate(field.definition?.unit || field.definition.unit, "unit") ?? "");
}

export const ScaleStringifier :IStringifier = function (field: DataFieldCache | ReadonlyDataField): string {
    if ((field.definition?.step || field.definition.step) && field.currentValues.value)
        return (field.currentValues.value * (field.definition?.step || field.definition.step)).toString();
    else return field.currentValues.value?.toString() || "";
}

export const ListEntryStringifier :IStringifier = function (field: DataFieldCache | ReadonlyDataField): string {
    if (field instanceof DataFieldCache) {
        return field.childFields.find((field: DataFieldCache | ReadonlyDataField) => field.identifier == "name")?.currentValues.value || "";
    }

    if (field instanceof ReadonlyDataField) {
        return field.childFields.find((field: DataFieldCache | ReadonlyDataField) => field.identifier == "name")?.currentValues.value || "";
    }

    return "";
}

export const DisplayValueStringifier :IStringifier = function (field: DataFieldCache | ReadonlyDataField): string {
    if (field.definition?.enum != null)
        return EnumerationStringifier(field)

    if (field.definition?.unit != null) {
        return ValueWithUnitStringifier(field);
    }
    
    if (field.currentValues?.year != null) {
        return DateStringifier(field);
    }

    return DefaultStringifier(field);
}
